import { AlertTriangle } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import { t, useLocale } from "@/shared/lib/i18n";
import { Button } from "./Button";
import { Dialog } from "./Overlay";

/**
 * Asking before something cannot be taken back.
 *
 * Before this existed, nothing in Exognitos confirmed anything. Deleting a
 * project — with every task under it — was one click in a menu, no dialog, no
 * undo, and the same was true of notes, routines, captures and reminders. The
 * only destructive action that was survivable was archiving, because archiving
 * is reversible and offered a toast.
 *
 * The rule this encodes: **reversible actions do not interrupt, irreversible
 * ones do.** Archive keeps its silent undo toast. Delete stops and says what
 * will be destroyed, in words that name the actual object — "Bu öğeyi silmek
 * istediğine emin misin?" is a question you learn to click through without
 * reading, because it would read identically for a typo and for a year of work.
 *
 * Two details matter more than the styling:
 *
 * - The safe choice takes focus on open, so Enter cancels. A confirmation whose
 *   default action is the destruction is a speed bump that removes itself.
 * - The consequence is stated as a fact and only when there is one ("Bu
 *   projedeki 12 görev de silinecek."). A dialog that always shouts is a dialog
 *   nobody reads by the third time.
 */

interface ConfirmRequest {
  /** What is being acted on, named. Becomes the dialog heading. */
  title: string;
  /** What will actually happen. Omit when the title already says everything. */
  description?: ReactNode;
  /** The destructive verb — "Sil", "Kalıcı olarak sil", "Sıfırla". */
  confirmLabel: string;
  /** Defaults to "Vazgeç". */
  cancelLabel?: string;
  /**
   * `danger` paints the confirm button critical, which is right for deletion
   * and wrong for a merely-significant action like resetting a week.
   */
  tone?: "danger" | "neutral";
}

type ConfirmFn = (request: ConfirmRequest) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  useLocale();
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  // Held in a ref rather than state: resolving is not a render concern, and
  // putting the resolver in state would make every confirmation two renders.
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setRequest(null);
  }, []);

  const confirm = useCallback<ConfirmFn>((next) => {
    // A second request while one is open resolves the first as declined —
    // nothing gets destroyed because a dialog was replaced underneath it.
    resolver.current?.(false);
    setRequest(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const danger = request?.tone !== "neutral";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        layerOrder={100}
        open={request !== null}
        onClose={() => settle(false)}
        size="sm"
        title={request?.title ?? ""}
        footer={
          <div className="flex justify-end gap-2">
            {/* Focused on mount, which is also when the dialog appears, so the
                keyboard default is the harmless one. */}
            <Button onClick={() => settle(false)} autoFocus>
              {request?.cancelLabel ?? t("copy.39a8d28c1c6b")}
            </Button>
            <Button variant={danger ? "danger" : "primary"} onClick={() => settle(true)}>
              {request?.confirmLabel ?? ""}
            </Button>
          </div>
        }
      >
        <div className="flex gap-3">
          {danger && (
            <span
              aria-hidden="true"
              className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-critical-soft text-critical"
            >
              <AlertTriangle className="size-4" />
            </span>
          )}
          <div className="min-w-0 text-sm leading-relaxed text-ink-muted">
            {request?.description}
            {danger && (
              <p className={request?.description ? "mt-1.5 text-ink-faint" : undefined}>
                {" "}
                {t("copy.b910012e68ff")}{" "}
              </p>
            )}
          </div>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error("useConfirm, ConfirmProvider içinde kullanılmalı.");
  return context;
}
